import React, { useRef, useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Button, IconButton,
  Divider, alpha, useTheme, Chip, Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../../app/features/store/store";
import { getFdpRoles, getFdcRoles, getOrganizationEmails } from "../../../../app/features/knowledgehub/knowledgehubSlice";
import { toast } from "react-toastify";

type RoleOption = { id: number; name: string };

export type UploadFilePayload = {
  description: string;
  fdpRoleIds: number[];
  fdcRoleIds: number[];
  manualEmails: string[];
  allUsers: false;
  files: File[];
};

type Props = {
  open: boolean;
  repositoryId: number;
  repositoryName: string;
  onClose: () => void;
  onSubmit: (repositoryId: number, payload: UploadFilePayload) => void;
};

const defaultForm = {
  description: "",
  fdpRoleIds: [] as number[],
  fdcRoleIds: [] as number[],
  manualEmails: [] as string[],
};

export default function KnowledgehubUploadFileModal({
  open, repositoryId, repositoryName, onClose, onSubmit,
}: Props) {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { fdpRoles, fdcRoles, organizationEmails } = useSelector((state: RootState) => state.knowledgehub);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [form, setForm] = useState({ ...defaultForm });
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [editEmailDialog, setEditEmailDialog] = useState<{ open: boolean; oldEmail: string; newEmail: string }>({ open: false, oldEmail: "", newEmail: "" });

  const ALLOWED_FILE_TYPES = [".pdf", ".doc", ".docx", ".ppt", ".pptx", ".mp4"];

  const validateFiles = (fileList: File[]): File[] => {
    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    fileList.forEach((file) => {
      const ext = "." + file.name.split(".").pop()?.toLowerCase();
      if (ALLOWED_FILE_TYPES.includes(ext)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length > 0) {
      toast.error(`Invalid file type: ${invalidFiles.join(", ")}. Only PDF, WORD, PPT, and MP4 files are allowed.`);
    }

    return validFiles;
  };

  useEffect(() => {
    if (open) {
      dispatch(getFdpRoles());
      dispatch(getFdcRoles());
      dispatch(getOrganizationEmails());
    }
  }, [open, dispatch]);

  const handleClose = () => {
    setFiles([]);
    setForm({ ...defaultForm });
    setEmailInput("");
    setEmailError("");
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    const validFiles = validateFiles(selected);
    setFiles((prev) => [...prev, ...validFiles]);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const validFiles = validateFiles(Array.from(e.dataTransfer.files));
    setFiles((prev) => [...prev, ...validFiles]);
  };

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const isEmailInOrganization = (email: string) => organizationEmails.includes(email.trim());

  const handleAddEmail = () => {
    const trimmed = emailInput.trim();
    if (!trimmed) return;
    if (!isValidEmail(trimmed)) { setEmailError("Please enter a valid email address"); return; }
    if (!isEmailInOrganization(trimmed)) { setEmailError("This email is not present in our organization"); return; }
    if (form.manualEmails.includes(trimmed)) { setEmailError("Email already added"); return; }
    setForm((prev) => ({ ...prev, manualEmails: [...prev.manualEmails, trimmed] }));
    setEmailInput("");
    setEmailError("");
  };

  const handleEditEmail = (email: string) => {
    setEditEmailDialog({ open: true, oldEmail: email, newEmail: email });
  };

  const handleSaveEditEmail = () => {
    const trimmed = editEmailDialog.newEmail.trim();
    if (!trimmed) return;
    if (!isValidEmail(trimmed)) return;
    if (!isEmailInOrganization(trimmed)) {
      toast.error("This email is not present in our organization");
      return;
    }
    if (trimmed !== editEmailDialog.oldEmail && form.manualEmails.includes(trimmed)) return;
    setForm((prev) => ({
      ...prev,
      manualEmails: prev.manualEmails.map((e) => e === editEmailDialog.oldEmail ? trimmed : e),
    }));
    setEditEmailDialog({ open: false, oldEmail: "", newEmail: "" });
  };

  const handleSubmit = () => {
    onSubmit(repositoryId, {
      description: form.description,
      fdpRoleIds: form.fdpRoleIds,
      fdcRoleIds: form.fdcRoleIds,
      manualEmails: form.manualEmails,
      allUsers: false,
      files,
    });
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Upload Files</Typography>
          <Typography variant="caption" color="text.secondary">
            Uploading to: <strong>{repositoryName}</strong>
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2.5, display: "flex", flexDirection: "column", gap: 2.5 }}>

        {/* Drop Zone */}
        <Box
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          sx={{
            border: `2px dashed ${dragOver ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.3)}`,
            borderRadius: 2,
            p: 3,
            textAlign: "center",
            cursor: "pointer",
            bgcolor: dragOver ? alpha(theme.palette.primary.main, 0.05) : "transparent",
            transition: "all 0.2s ease",
            "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) },
          }}
        >
          <UploadFileIcon sx={{ fontSize: 36, color: alpha(theme.palette.primary.main, 0.5), mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Drag & drop files here or <strong style={{ color: theme.palette.primary.main }}>browse</strong>
          </Typography>
          <Typography variant="caption" color="text.disabled">
            PDF, WORD, PPT, MP4 supported
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            multiple
            accept=".pdf,.doc,.docx,.ppt,.pptx,.mp4"
            onChange={handleFileChange}
          />
        </Box>

        {/* Selected Files */}
        {files.length > 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {files.map((file, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  px: 1.5, py: 1, borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                  <Chip label={file.name.split(".").pop()?.toUpperCase()} size="small" sx={{ fontSize: "0.65rem", fontWeight: 600 }} />
                  <Typography variant="body2" noWrap>{file.name}</Typography>
                </Box>
                <IconButton size="small" onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))} sx={{ color: "error.main" }}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        {/* Description */}
        <TextField
          label="Description"
          value={form.description}
          onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
          fullWidth
          multiline
          rows={2}
          size="small"
          placeholder="Brief description of the uploaded files"
        />

        {/* FDP Roles */}
        <Autocomplete
          multiple
          options={fdpRoles}
          getOptionLabel={(o) => o.name}
          value={fdpRoles.filter((r) => form.fdpRoleIds.includes(r.id))}
          onChange={(_, selected) =>
            setForm((prev) => ({ ...prev, fdpRoleIds: selected.map((r: RoleOption) => r.id) }))
          }
          renderInput={(params) => (
            <TextField {...params} label="FDP Roles" size="small" placeholder="Select FDP roles" />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                label={option.name}
                size="small"
                {...getTagProps({ index })}
                sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}
              />
            ))
          }
        />

        {/* FDC Roles */}
        <Autocomplete
          multiple
          options={fdcRoles}
          getOptionLabel={(o) => o.name}
          value={fdcRoles.filter((r) => form.fdcRoleIds.includes(r.id))}
          onChange={(_, selected) =>
            setForm((prev) => ({ ...prev, fdcRoleIds: selected.map((r: RoleOption) => r.id) }))
          }
          renderInput={(params) => (
            <TextField {...params} label="FDC Roles" size="small" placeholder="Select FDC roles" />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                label={option.name}
                size="small"
                {...getTagProps({ index })}
                sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }}
              />
            ))
          }
        />

        {/* Manual Emails */}
        <Box>
          <Autocomplete
            freeSolo
            options={organizationEmails || []}
            value={emailInput}
            onInputChange={(_, newValue) => {
              setEmailInput(newValue || "");
              setEmailError("");
            }}
            getOptionLabel={(option) => option}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Add Email"
                size="small"
                placeholder="e.g. user@frontdoorcorp.com"
                error={!!emailError}
                helperText={emailError || "Press Enter or click + to add"}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddEmail(); } }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {params.InputProps.endAdornment}
                      <IconButton
                        onClick={handleAddEmail}
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          mr: 0.5,
                          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.2) },
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </>
                  ),
                }}
              />
            )}
          />
          {form.manualEmails.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mt: 1.5 }}>
              {form.manualEmails.map((email) => (
                <Chip
                  key={email}
                  label={email}
                  size="small"
                  onDelete={() => setForm((prev) => ({ ...prev, manualEmails: prev.manualEmails.filter((m) => m !== email) }))}
                  onClick={() => handleEditEmail(email)}
                  icon={<EditIcon sx={{ fontSize: "0.9rem" }} />}
                  sx={{ bgcolor: alpha(theme.palette.success.main, 0.08), color: "text.primary", cursor: "pointer" }}
                />
              ))}
            </Box>
          )}
        </Box>

      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" size="small">Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          size="small"
          disabled={files.length === 0}
          startIcon={<UploadFileIcon />}
        >
          Upload
        </Button>
      </DialogActions>

      {/* Edit Email Dialog */}
      <Dialog
        open={editEmailDialog.open}
        onClose={() => setEditEmailDialog({ open: false, oldEmail: "", newEmail: "" })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6" fontWeight={600}>Edit Email</Typography>
          <IconButton onClick={() => setEditEmailDialog({ open: false, oldEmail: "", newEmail: "" })} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Autocomplete
            freeSolo
            options={organizationEmails || []}
            value={editEmailDialog.newEmail}
            onInputChange={(_, newValue) => {
              setEditEmailDialog((prev) => ({ ...prev, newEmail: newValue || "" }));
            }}
            getOptionLabel={(option) => option}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Email Address"
                size="small"
                autoFocus
                error={editEmailDialog.newEmail.trim() !== "" && (!isValidEmail(editEmailDialog.newEmail) || !isEmailInOrganization(editEmailDialog.newEmail))}
                helperText={
                  editEmailDialog.newEmail.trim() !== "" && !isValidEmail(editEmailDialog.newEmail)
                    ? "Invalid email format"
                    : editEmailDialog.newEmail.trim() !== "" && !isEmailInOrganization(editEmailDialog.newEmail)
                    ? "This email is not present in our organization"
                    : ""
                }
              />
            )}
          />
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditEmailDialog({ open: false, oldEmail: "", newEmail: "" })} variant="outlined" size="small">
            Cancel
          </Button>
          <Button
            onClick={handleSaveEditEmail}
            variant="contained"
            size="small"
            disabled={!editEmailDialog.newEmail.trim() || !isValidEmail(editEmailDialog.newEmail) || !isEmailInOrganization(editEmailDialog.newEmail)}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
